import type { SelectOption } from '@kanda-libs/ks-component-ts';
import type {
  FinanceProvider,
  FinanceRate,
  LenderConfig,
  Rate,
} from '@kanda-libs/ks-frontend-services';

import { capitaliseWords } from 'common/helpers';

export interface RateFormRow extends FinanceRate {
  selected: boolean;
}

export interface CustomRatesFormValues {
  provider: FinanceProvider | '';
  rates: RateFormRow[];
  xid: string;
}

// Whether the selected lender requires a per-company external user id (xid).
export const requiresUserId = (
  provider: FinanceProvider | '',
  providersRequiringUserId: FinanceProvider[],
): boolean =>
  Boolean(provider) &&
  providersRequiringUserId.includes(provider as FinanceProvider);

// The existing xid stored for a provider in the company's lender_configs.
export const getExistingXid = (
  lenderConfigs: LenderConfig[] | undefined,
  provider: FinanceProvider | '',
): string => {
  if (!provider) return '';
  return (
    (lenderConfigs || []).find((config) => config.provider === provider)?.xid ||
    ''
  );
};

// Upsert a lender config by provider: replace an existing entry for the same
// provider, otherwise append. Other providers' configs are left untouched.
export const upsertLenderConfig = (
  lenderConfigs: LenderConfig[] | undefined,
  provider: FinanceProvider,
  xid: string,
): LenderConfig[] => [
  ...(lenderConfigs || []).filter((config) => config.provider !== provider),
  { provider, xid },
];

export const getAllRates = (rate?: Rate): FinanceRate[] =>
  rate?.finance_rates || [];

export const getProviderOptions = (allRates: FinanceRate[]): SelectOption[] => {
  const providers = Array.from(
    new Set(allRates.map((rate) => rate.provider)),
  ).sort((a, b) => a.localeCompare(b));

  return [
    { name: 'Select a lender...', value: '' },
    ...providers.map((provider) => ({
      name: capitaliseWords(provider.replace(/_/g, ' ')),
      value: provider,
    })),
  ];
};

export const buildRateRows = (
  allRates: FinanceRate[],
  provider: FinanceProvider | '',
  existing: FinanceRate[],
): RateFormRow[] => {
  if (!provider) return [];

  return allRates
    .filter((rate) => rate.provider === provider)
    .map((rate) => {
      // match on provider too: rate names overlap across lenders (e.g. humm and
      // allium share some), so a name-only match would wrongly pre-tick another
      // lender's saved rates when switching lenders.
      const custom = existing.find(
        (r) => r.name === rate.name && r.provider === rate.provider,
      );
      return {
        ...rate,
        fee: custom?.fee ?? rate.fee,
        selected: Boolean(custom),
      };
    });
};

export const formatRateBody = (rows: RateFormRow[]): Rate => {
  const financeRates = rows
    .filter((row) => row.selected)
    .map(({ selected, ...rate }) => ({
      ...rate,
      enabled: true,
    }));

  return { finance_rates: financeRates };
};
export const hasIntersection = <T>(arr1: T[], arr2: T[]): boolean => {
  const set2 = new Set(arr2);
  return arr1.some((item) => set2.has(item));
};
